import { AxiosError } from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { AnimatePresence, m as motion } from 'framer-motion';
import { FocusEventHandler, forwardRef, useEffect, useRef, useState } from 'react';
import { AnyObject, StringSchema, addMethod, number, object, string } from 'yup';
import {
    ApiParticipantsListRequest,
    AppParticipantsListResponse,
    ParticipantsListResponseData,
    postParticipantsList,
} from '@/shared/api/post-participants-list';
import Button from '@/shared/components/shared/Button';
import Checkbox from '@/shared/components/shared/Checkbox';
import Image from '@/shared/components/shared/Image';
import Input from '@/shared/components/shared/Input';
import PhoneInput from '@/shared/components/shared/PhoneInput';
import Typografed from '@/shared/components/shared/Typografed';
import Video from '@/shared/components/shared/Video';
import { DICT } from '@/shared/config/dict';
import { useLocale } from '@/shared/hooks/use-locale';
import { useGetUtmParams } from '@/shared/hooks/use-utm/use-get-utm';
import { ImageShape, VideoShape } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { apiAssetUrl } from '@/shared/lib/utils/api-asset-url';
import { formatPhoneHref } from '@/shared/lib/utils/strings';
import { isPhone } from '@/shared/lib/utils/validation/phone';

addMethod(string, 'customPhone', function (errorMessage) {
    return this.test(`test-custom-phone`, errorMessage, function (value) {
        return typeof value === 'string' ? isPhone(value) : false;
    });
});

export type ParticipantsListSubmitFn = (body: ApiParticipantsListRequest) => Promise<AppParticipantsListResponse>;

export type RawProps = {
    title: string;
    description: string;
    video: VideoShape;
    poster: ImageShape;
    logos: ImageShape[];
    submitFn?: ParticipantsListSubmitFn;
};

type Props = React.HTMLAttributes<HTMLElement> & RawProps;

type FormResponse = {
    status: 'success' | 'error';
} & ParticipantsListResponseData;

const SUCCESS_RESET_MS = 10000;

const bodyMotion = {
    initial: { opacity: 0, y: -5 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 5 },
};

const ParticipantsListForm = forwardRef<HTMLDivElement, Props>(
    ({ title, description, video, poster, logos, submitFn = postParticipantsList, ...props }, ref) => {
        const locale = useLocale();
        const getUtmParams = useGetUtmParams();
        const [response, setResponse] = useState<FormResponse | null>(null);
        const successTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

        useEffect(() => {
            return () => {
                clearTimeout(successTimeoutRef.current);
            };
        }, []);

        const schema = object({
            name: string().required('Поле обязательно к заполнению'),
            email: string()
                .required('Поле обязательно к заполнению')
                .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, 'Введите корректный адрес эл. почты'),
            phone: (
                string().required('Поле обязательно к заполнению') as StringSchema<string, AnyObject, undefined, ''> & {
                    customPhone: any;
                }
            ).customPhone('Номер телефона введен некорректно'),
            agreementChecked: number().oneOf([1], DICT.FILL_CHECKED[locale]),
        });

        const {
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            errors,
            touched,
            isSubmitting,
            setFieldTouched,
            setFieldValue,
            setValues,
            setTouched,
            initialTouched,
            initialValues,
        } = useFormik({
            initialValues: {
                name: '',
                email: '',
                phone: '',
                agreementChecked: 0,
            },
            validationSchema: schema,
            onSubmit: async (formValues) => {
                clearTimeout(successTimeoutRef.current);
                setResponse(null);

                try {
                    const data = await submitFn({
                        name: formValues.name,
                        email: formValues.email,
                        phone: formatPhoneHref(formValues.phone).replace('+', ''),
                        agreementChecked: formValues.agreementChecked,
                        agreementCheckedProcessingPersonalData: formValues.agreementChecked,
                        ...Object.fromEntries(Object.entries(getUtmParams).filter(([_, value]) => value)),
                    });

                    if (data.status === 'success') {
                        setResponse({
                            status: 'success',
                            title: data.data?.title ?? '',
                            description: data.data?.description ?? '',
                        });

                        setValues(initialValues);
                        setTouched(initialTouched);

                        successTimeoutRef.current = setTimeout(() => {
                            setResponse(null);
                        }, SUCCESS_RESET_MS);
                    } else {
                        setResponse({
                            status: 'error',
                            title: data.data?.title ?? 'Не удалось отправить заявку',
                            description:
                                data.data?.description ??
                                'Произошла техническая ошибка. Попробуйте отправить форму еще раз через несколько минут.',
                        });
                    }
                } catch (err) {
                    if (err instanceof AxiosError) {
                        setResponse({
                            status: 'error',
                            title: err.response?.data?.data?.title ?? 'Не удалось отправить заявку',
                            description:
                                err.response?.data?.data?.message ??
                                err.response?.data?.errors?.flat().join('<br>') ??
                                'Произошла техническая ошибка. Попробуйте отправить форму еще раз через несколько минут.',
                        });
                    } else {
                        throw err;
                    }
                }
            },
        });

        const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
            setFieldTouched(event.target.name, false);
        };

        const viewKey = response?.status ?? 'form';

        return (
            <div
                {...props}
                ref={ref}
                className={classNames(
                    'participants-list-form',
                    {
                        'participants-list-form--error': response?.status === 'error',
                    },
                    props.className,
                )}
            >
                <div className="participants-list-form__media" aria-hidden="true">
                    <Video
                        sources={video}
                        className="participants-list-form__video"
                        preload="metadata"
                        autoPlay
                        muted
                        playsInline
                        loop
                        previewImg={poster}
                    />
                    <div className="participants-list-form__overlay" />
                </div>

                <div className="participants-list-form__content">
                    <div className="participants-list-form__header">
                        <h2 className="participants-list-form__title h3">
                            <Typografed>{title}</Typografed>
                        </h2>
                        <p className="participants-list-form__description text-m">
                            <Typografed>{description}</Typografed>
                        </p>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={viewKey}
                            className="participants-list-form__body"
                            initial={bodyMotion.initial}
                            animate={bodyMotion.animate}
                            exit={bodyMotion.exit}
                        >
                            {response?.status === 'success' && (
                                <div className="participants-list-form__result">
                                    <div
                                        className="participants-list-form__result-title text-m"
                                        dangerouslySetInnerHTML={{ __html: tp(response.title) }}
                                    />
                                    <div
                                        className="participants-list-form__result-text text-xs wysiwyg"
                                        dangerouslySetInnerHTML={{ __html: tp(response.description) }}
                                    />
                                </div>
                            )}

                            {response?.status === 'error' && (
                                <div className="participants-list-form__result participants-list-form__result--error">
                                    <div className="participants-list-form__result-texts">
                                        <div
                                            className="participants-list-form__result-title text-m"
                                            dangerouslySetInnerHTML={{ __html: tp(response.title) }}
                                        />
                                        <div
                                            className="participants-list-form__result-text text-xs wysiwyg"
                                            dangerouslySetInnerHTML={{ __html: tp(response.description) }}
                                        />
                                    </div>
                                    <div className="participants-list-form__result-actions">
                                        <Button
                                            type="button"
                                            variant="outline-light-filled"
                                            className="text-xs participants-list-form__result-action-btn"
                                            onClick={() => setResponse(null)}
                                        >
                                            {DICT.RESEND[locale]}
                                        </Button>
                                        <a
                                            href="https://t.me/tnf_oilgasforum_bot"
                                            className="participants-list-form__support text-xs"
                                            target="_blank"
                                            rel="noreferrer nofollow"
                                        >
                                            {DICT.SUPPORT_SERVICE[locale]}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {!response && (
                                <form className="participants-list-form__form" noValidate onSubmit={handleSubmit}>
                                    <div className="participants-list-form__fields text-s">
                                        <div className="participants-list-form__field">
                                            <div className="participants-list-form__label">Имя</div>
                                            <Input
                                                label={values.name}
                                                type="text"
                                                name="name"
                                                className="participants-list-form__input"
                                                id="participants-list-name"
                                                placeholder="Иван"
                                                value={values.name}
                                                onInput={handleChange}
                                                onChange={handleChange}
                                                onFocus={onFocus}
                                                onBlur={handleBlur}
                                                message={touched.name ? errors.name : ''}
                                                valid={!(touched.name && errors.name)}
                                                hideLabel
                                                maxLength={255}
                                            />
                                        </div>
                                        <div className="participants-list-form__field">
                                            <div className="participants-list-form__label">Email</div>
                                            <Input
                                                className="participants-list-form__input"
                                                id="participants-list-email"
                                                label={values.email}
                                                type="text"
                                                name="email"
                                                placeholder="tnf2024@gmail.com"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                onInput={handleChange}
                                                onFocus={onFocus}
                                                message={touched.email ? errors.email : ''}
                                                valid={!(touched.email && errors.email)}
                                                hideLabel
                                                maxLength={255}
                                            />
                                        </div>
                                        <div className="participants-list-form__field">
                                            <div className="participants-list-form__label">Телефон</div>
                                            <PhoneInput
                                                className="participants-list-form__input"
                                                id="participants-list-phone"
                                                label={values.phone}
                                                name="phone"
                                                placeholder="+7(999)999-99-99"
                                                value={values.phone}
                                                onPhoneChange={(value) => setFieldValue('phone', value)}
                                                onBlur={handleBlur}
                                                onFocus={onFocus}
                                                message={touched.phone ? errors.phone : ''}
                                                valid={!(touched.phone && errors.phone)}
                                                hideLabel
                                            />
                                        </div>
                                    </div>

                                    <div className="participants-list-form__footer">
                                        <Checkbox
                                            name="agreementChecked"
                                            checkboxStyle="square"
                                            checked={values.agreementChecked === 1}
                                            onChange={(e) => {
                                                setFieldTouched('agreementChecked', true);
                                                handleChange({
                                                    target: {
                                                        name: e.target.name,
                                                        value: e.target.checked ? 1 : 0,
                                                    },
                                                });
                                            }}
                                            onBlur={handleBlur}
                                            valid={!errors.agreementChecked}
                                            className="participants-list-form__checkbox"
                                        >
                                            <span
                                                className="participants-list-form__checkbox-text text-xs"
                                                dangerouslySetInnerHTML={{
                                                    __html: tp(
                                                        'Нажимая кнопку, Вы даете <a href="/agreement/" class="link link--underlined" target="_blank" rel="noreferrer nofollow">Согласие</a> на обработку персональных данных в соответствии с <a href="/privacy/" class="link link--underlined" target="_blank" rel="noreferrer nofollow">Политикой</a> и принимаете <a href="/privacy/" class="link link--underlined" target="_blank" rel="noreferrer nofollow">пользовательское соглашение</a>',
                                                    ),
                                                }}
                                            />
                                        </Checkbox>

                                        <Button
                                            type="submit"
                                            variant="outline-light-filled"
                                            className="text-xs"
                                            disabled={isSubmitting}
                                            asyncActionActive={isSubmitting}
                                        >
                                            Получить материалы
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {logos.length === 1 && (
                    <div className="participants-list-form__logo-single">
                        <Image
                            className="participants-list-form__logo-single-img"
                            src={apiAssetUrl(logos[0].src)}
                            alt={logos[0].alt || ''}
                            width={logos[0].width || 300}
                            height={logos[0].height || 80}
                        />
                    </div>
                )}

                {logos.length > 1 && (
                    <ul className="participants-list-form__logos list-unstyled">
                        {logos.map((logo, i) => (
                            <li className="participants-list-form__logo" key={i}>
                                <span
                                    className="participants-list-form__logo-el"
                                    role="img"
                                    aria-label={logo.alt || ''}
                                    style={
                                        {
                                            '--icon-url': `url(${apiAssetUrl(logo.src)})`,
                                        } as React.CSSProperties
                                    }
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    },
);

ParticipantsListForm.displayName = 'ParticipantsListForm';

export default ParticipantsListForm;
